import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common'
import { booleanAttribute, Component, Input, input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges, viewChild } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms'
import { Network } from '@capacitor/network'
import { IonButton, IonIcon, IonModal, IonSpinner } from '@ionic/angular/standalone'
import { TranslatePipe } from '@ngx-translate/core'
import { addIcons } from 'ionicons'
import { add, backspaceOutline, close, closeCircleOutline, remove } from 'ionicons/icons'
import { combineLatest, firstValueFrom, map, Observable, Subscription } from 'rxjs'

import { HOME_SPORT_GROUPS } from '../../../pages/home/constants/home-sport-groups.constant'
import { validOddValidator } from '../../../pages/my-bets/components/unmatched-collapsible/validators/valid-odd.validator'
import { V3PlaceBetViewComponent } from '../../../skins/v3/components/v3-place-bet-view/v3-place-bet-view.component'
import { PLACE_BET_TIMEOUT } from '../../constants/common.constant'
import { FeatureFlagDirective } from '../../directives/feature-flag.directive'
import { BalanceService } from '../../services/balance.service'
import { BetService } from '../../services/bet.service'
import { BetConfigService } from '../../services/bet-config.service'
import { ExposureService } from '../../services/exposure.service'
import { FeatureFlagService } from '../../services/feature-flag.service'
import { HelperService } from '../../services/helper.service'
import { LiveUpdateService } from '../../services/live-update.service'
import { MarketService } from '../../services/market.service'
import { WhiteLabelService } from '../../services/white-label.service'
import { WhiteLabelMarketConfigService } from '../../services/white-label-market-config.service'
import { Market, MarketProvider, PredictedBet } from '../../types/market.type'
import { formatBetResponseMessage } from '../../utils/bet-response-message.util'
import { AlertService } from '../alert/alert.service'
import { AhLadderPnlComponent } from '../asian-handicap-pnl/ah-ladder-pnl/ah-ladder-pnl.component'
import { DebuggingService } from '../debugging-modal/services/debugging.service'
import { FormErrorMessageComponent } from '../form-error-message/form-error-message.component'
import { ToastService } from '../toast/toast.service'
import { TOAST_STATE } from '../toast/types/toast.type'
import { EditStakeModalComponent } from './components/edit-stake-modal/edit-stake-modal.component'
import { EditStakeModalService } from './components/edit-stake-modal/edit-stake-modal.service'
import { PlaceBetModalService } from './place-bet-modal.service'
import { PlaceBetRequest, PlaceBetWebsocketResponse } from './types/place-bet.type'
import { SelectedRunner } from './types/selected-runner.type'

type CompactRunnerPnl = number | number[] | { result: number } | null

@Component({
	selector: 'app-place-bet-modal',
	templateUrl: './place-bet-modal.component.html',
	styleUrls: ['./place-bet-modal.component.scss'],
	standalone: true,
	imports: [
		IonModal,
		IonIcon,
		ReactiveFormsModule,
		AsyncPipe,
		TranslatePipe,
		FeatureFlagDirective,
		IonButton,
		IonSpinner,
		FormErrorMessageComponent,
		NgClass,
		AhLadderPnlComponent,
		DecimalPipe,
		EditStakeModalComponent,
		V3PlaceBetViewComponent,
	],
})
export class PlaceBetModalComponent implements OnInit, OnChanges, OnDestroy {
	subscriptions$ = new Subscription()

	private readonly compactProfitAndLossFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })

	private v3StakeKeypadValue = ''

	readonly v3KeypadKeys = ['1', '2', '3', '4', '5', '6', 'delete', '7', '8', '9', '0', '00', '.']

	@Input() additionalStakeValidators: ValidatorFn[] = []

	@Input() renderMode: 'modal' | 'inline' = 'modal'

	@Input({ transform: booleanAttribute }) inlineBleed = false

	market = input.required<Market>()

	selectedRunner = input.required<SelectedRunner>()

	placeBetModal = viewChild<HTMLIonModalElement>('placeBetModal')

	profitOrLiability = signal<number | null>(null)

	insufficientBalanceError = signal<{ insufficientBalance: boolean } | null>(null)

	minBetSize = signal<number | null>(null)

	maxBetSize = signal<number | null>(null)

	shouldCancelUnmatchedBetsFirst = signal<boolean>(false)

	placeBetForm = new FormGroup({
		odd: new FormControl<number | null>(null, { validators: [Validators.required], nonNullable: true }),
		stake: new FormControl<number | null>(null, {
			nonNullable: true,
			updateOn: 'change',
		}), // Input model for the stake text field
	})

	betPlacementTimeout: ReturnType<typeof setTimeout> | null = null // Holds the timeout object for bet placement. It is cleared after the websocket response is read or after the timout has elapsed

	placedCustomerOrderRef: string | null = null // Holds the bet ID that has just been placed

	stakeValidators: Record<string, ValidatorFn | ValidatorFn[]> = { required: Validators.required }

	// Check if placing bet in another tab
	get isPlacingInAnotherTab(): boolean {
		return localStorage.getItem('placing_bet_flag') === 'true'
	}

	get isPlaceBetDisabled(): boolean {
		return (
			this.placeBetForm.invalid ||
			this.betService.isPlacingBet() ||
			this.isPlacingInAnotherTab ||
			Boolean(this.insufficientBalanceError()) ||
			this.shouldCancelUnmatchedBetsFirst()
		)
	}

	constructor(
		private readonly toastService: ToastService,
		private readonly liveUpdateService: LiveUpdateService,
		private readonly marketService: MarketService,
		private readonly exposureService: ExposureService,
		protected readonly debuggingService: DebuggingService,
		protected readonly betService: BetService,
		protected readonly helperService: HelperService,
		protected readonly alertService: AlertService,
		protected readonly editStakeModalService: EditStakeModalService,
		protected readonly balanceService: BalanceService,
		protected readonly placeBetModalService: PlaceBetModalService,
		protected readonly whiteLabelService: WhiteLabelService,
		protected readonly featureFlagService: FeatureFlagService,
		private readonly betConfigService: BetConfigService,
		private readonly whiteLabelMarketConfigService: WhiteLabelMarketConfigService,
	) {
		addIcons({ closeCircleOutline, add, remove, close, backspaceOutline })
		this.subscribeToExposureEvaluation()
	}

	get formControls() {
		return this.placeBetForm.controls
	}

	get isLineMarket() {
		return this.helperService.isLineMarket(this.market())
	}

	get shouldOddBeReadonly(): boolean {
		return (
			this.isLineMarket || this.selectedRunner().provider === MarketProvider.SB || this.whiteLabelMarketConfigService.showFeature('disableUnmatchedBets')
		)
	}

	get errorMessage(): string | null {
		if (this.shouldCancelUnmatchedBetsFirst()) {
			return 'Cancel unmatched bets first'
		}
		const balanceError = this.insufficientBalanceError()
		if (balanceError && 'insufficientBalance' in balanceError && balanceError.insufficientBalance) {
			return 'Insufficient balance'
		}
		return null
	}

	get isHandicapTypeMarket() {
		return this.helperService.isAsianHandicapMarket(this.market()) || this.helperService.isGoalLinesMarket(this.market())
	}

	get shouldShowExposureDebugging(): boolean {
		return this.featureFlagService.getFeature('exposureCalculation')
	}

	ngOnInit() {
		this.subscribeToFormControlsValueChanges()
		this.updateFormControlValues()
		this.setValidators()
		localStorage.removeItem('placing_bet_flag')
		this.subscriptions$.add(
			this.betConfigService.betConfig$.subscribe((betConfig) => {
				this.setBetConfigValidators(betConfig)
			}),
		)
	}

	ngOnChanges(changes: SimpleChanges): void {
		const selectedRunnerChange = changes['selectedRunner']

		// Update the odd field only when there is a change between the display odds
		if (selectedRunnerChange?.currentValue?.dfOdds && selectedRunnerChange?.currentValue?.dfOdds !== selectedRunnerChange?.previousValue?.dfOdds) {
			this.placeBetForm.patchValue({ odd: selectedRunnerChange.currentValue.dfOdds })
		}

		if (
			(selectedRunnerChange?.currentValue?.minBet && selectedRunnerChange?.currentValue?.minBet !== selectedRunnerChange?.previousValue?.minBet) ||
			(selectedRunnerChange?.currentValue?.maxBet && selectedRunnerChange?.currentValue?.maxBet !== selectedRunnerChange?.previousValue?.maxBet) ||
			(selectedRunnerChange?.currentValue?.betAllow && selectedRunnerChange?.currentValue?.betAllow !== selectedRunnerChange?.previousValue?.betAllow)
		) {
			this.placeBetForm.patchValue({ stake: selectedRunnerChange.currentValue.stake })
		}

		const additionalStakeValidatorsChange = changes['additionalStakeValidators']
		if (additionalStakeValidatorsChange && additionalStakeValidatorsChange.currentValue !== additionalStakeValidatorsChange.previousValue) {
			this.stakeValidators['additionalStakeValidators'] = additionalStakeValidatorsChange.currentValue
			this.formControls.stake.setValidators(Object.values(this.stakeValidators).flat())
			this.formControls.stake.updateValueAndValidity()
		}
	}

	ngOnDestroy(): void {
		// Remove the current predicted bet from the list of predicted bets
		this.removeCurrentPredictedBet()
		this.subscriptions$.unsubscribe()
		if (this.betPlacementTimeout) {
			clearTimeout(this.betPlacementTimeout)
			this.betPlacementTimeout = null
		}
	}

	/**
	 * Handle bet placing functionality
	 */
	async placeBet() {
		// Check cross-tab protection first
		if (localStorage.getItem('placing_bet_flag') === 'true') {
			return
		}

		this.formControls.stake.markAsTouched()
		this.formControls.stake.markAsDirty()

		const userId = this.helperService.getUserData()?.user_fields?.ID

		if (this.isLineMarket && !this.isHandicapTypeMarket) {
			let formOddValue = 0
			let updateOddValue = 0
			if (this.selectedRunner().provider === MarketProvider.BF) {
				const marketUpdate = this.marketService.bfMarketUpdates$.getValue()[this.selectedRunner().marketId]
				const runner = marketUpdate.r.find((r) => r.id === this.selectedRunner().selectionId)

				if (this.selectedRunner().provider === MarketProvider.BF && runner) {
					if (this.selectedRunner().side === 'BACK') {
						formOddValue = Number(this.formControls.odd.value ?? 0)
						updateOddValue = runner.ex.abp[0]
					} else {
						formOddValue = Number(this.formControls.odd.value ?? 0)
						updateOddValue = runner.ex.alp[0]
					}
				}
			} else if (this.selectedRunner().provider === MarketProvider.SB) {
				const marketUpdate = this.marketService.fancyUpdates$.getValue()[this.selectedRunner().topic ?? '']
				const runner = marketUpdate?.runners?.find((runner) => Number(runner.selectionId) === this.selectedRunner().selectionId)

				if (runner) {
					if (this.selectedRunner().side === 'BACK') {
						formOddValue = Number(this.formControls.odd.value ?? 0)
						updateOddValue = runner.priceYes
					} else {
						formOddValue = Number(this.formControls.odd.value ?? 0)
						updateOddValue = runner.priceNo
					}
				}
			}

			if (formOddValue !== updateOddValue) {
				this.alertService.showAlert(
					'Odds have changed. Do you want to retry?',
					() => {
						this.placeBetForm.patchValue({ odd: updateOddValue })
						this.alertService.dismissAlert()
					},
					() => {
						this.alertService.dismissAlert()
					},
				)

				return
			}
		}

		// await this.loadingService.showLoading(PLACE_BET_TIMEOUT)
		this.betService.isPlacingBet.set(true)
		if (userId && this.selectedRunner() && this.placeBetForm.valid) {
			const placeBetRequestData: PlaceBetRequest = {
				PRICE: this.selectedRunner().runs ?? Number(this.placeBetForm.controls.odd.value!),
				SIZE: Number(this.placeBetForm.controls.stake.value!),
				SIDE: this.selectedRunner().side,
				MARKET_ID: this.selectedRunner().marketId,
				SPORT_ID: this.selectedRunner().sportId,
				EVENT_ID: this.selectedRunner().eventId,
				PERSISTENCE_TYPE:
					this.selectedRunner().sportId === HOME_SPORT_GROUPS.GREYHOUND_RACING.toString() || this.market().description?.['marketType'] === 'PLACE'
						? 'LAPSE'
						: this.helperService.getUserBetslipSettings().betSlipSettings.persistenceType,
				SELECTION_ID: this.selectedRunner().selectionId,
				HANDICAP: this.selectedRunner().handicap,
				USER_ID: userId,
				BETTING_TYPE: this.selectedRunner().bettingType
					? this.selectedRunner().bettingType.includes('ODDS')
						? 'ODDS'
						: this.selectedRunner().bettingType
					: 'ODDS',
				PROVIDER: this.selectedRunner().provider,
				PROVIDER_ID: this.selectedRunner().providerId,
				SERVICE_ID: this.selectedRunner().serviceId,
				F_ODDS: this.helperService.getFinalOdds(
					this.selectedRunner().provider === MarketProvider.SB && this.selectedRunner().bettingType === 'LINE'
						? this.selectedRunner().dfOdds
						: Number(this.placeBetForm.controls.odd.value!),
					this.selectedRunner().autoBm ? MarketProvider.BF : this.selectedRunner().provider,
					this.selectedRunner().bettingType === 'LINE',
				),
			}

			// If the bet is not successfully placed within 10 seconds, show error message and unsubscribe the added subscriptions$
			if (this.betPlacementTimeout) {
				clearTimeout(this.betPlacementTimeout)
			}
			this.betPlacementTimeout = setTimeout(() => {
				void this.handleBetPlacementTimeout(this.placedCustomerOrderRef)
			}, PLACE_BET_TIMEOUT)

			this.subscriptions$.add(
				this.placeBetModalService.placeBet(placeBetRequestData).subscribe({
					next: (placeBetResult) => {
						if (placeBetResult.status === 'ok') {
							this.placedCustomerOrderRef = placeBetResult.result[0]?.ID?.toString()
							this.subscriptions$.add(
								this.liveUpdateService.getData<PlaceBetWebsocketResponse>().subscribe({
									next: (placeBetWebsocketResponse: PlaceBetWebsocketResponse) => {
										if (placeBetWebsocketResponse && placeBetWebsocketResponse.type === 'bet_placed') {
											void this.handleBetPlacementResponse(placeBetWebsocketResponse, placeBetResult.result[0]?.ID)
										}
									},
									error: (e) => {
										console.error({ betPlacementError: e })
										this.toastService.showToast(TOAST_STATE.DANGER, 'Failed: error in fetching websocket response')
										// await this.loadingService.closeLoading()
										this.betService.isPlacingBet.set(false)
										if (this.betPlacementTimeout) clearTimeout(this.betPlacementTimeout)
									},
								}),
							)
						} else {
							this.toastService.showToast(TOAST_STATE.DANGER, `Failed: ${formatBetResponseMessage(placeBetResult.result[0])}.`)
							// await this.loadingService.closeLoading()
							this.betService.isPlacingBet.set(false)
							if (this.betPlacementTimeout) clearTimeout(this.betPlacementTimeout)
						}
					},
					error: (err) => {
						console.error({ betPlacementError: err })
						this.toastService.showToast(TOAST_STATE.DANGER, 'Failed to place bet')
						// await this.loadingService.closeLoading()
						this.betService.isPlacingBet.set(false)
						if (this.betPlacementTimeout) clearTimeout(this.betPlacementTimeout)
					},
				}),
			)
		}
	}

	/**
	 * Close the bet placing window for the respective match row
	 */
	closeBetPlacingWindow() {
		if (this.renderMode === 'inline') {
			this.afterModalDismissal()
			return
		}

		this.placeBetModal()?.dismiss()
	}

	afterModalDismissal() {
		this.removeCurrentPredictedBet()
		this.placeBetModalService.dismissPlaceBetModal()
	}

	/**
	 * Formats quick amounts ending in three zeros to use a 'k' suffix.
	 * E.g., '1000' -> '1k', '5000' -> '5k', '25000' -> '25k'.
	 */
	formatQuickAmount(amount: string | number): string {
		const num = Number(amount)
		if (!isNaN(num) && num >= 1000 && num % 1000 === 0) {
			return `${num / 1000}k`
		}
		return String(amount)
	}

	/**
	 * Increment the value of the placed stake by a given amount
	 * @param amount
	 */
	setStakeAmount(amount: string) {
		this.v3StakeKeypadValue = String(this.helperService.getNumericValue(amount))
		this.placeBetForm.patchValue({ stake: this.helperService.getNumericValue(amount) })
		this.placeBetForm.controls.stake.markAsDirty()
		this.placeBetForm.controls.stake.markAsTouched()
	}

	applyV3KeypadValue(key: string) {
		const currentValue = this.placeBetForm.controls.stake.value
		const shouldSyncFromForm =
			!this.v3StakeKeypadValue || (currentValue !== null && Number(this.v3StakeKeypadValue) !== currentValue && !this.v3StakeKeypadValue.endsWith('.'))

		if (shouldSyncFromForm) {
			this.v3StakeKeypadValue = currentValue === null ? '' : String(currentValue)
		}

		if (key === 'delete') {
			this.v3StakeKeypadValue = this.v3StakeKeypadValue.slice(0, -1)
		} else if (key === '.' && !this.v3StakeKeypadValue.includes('.')) {
			this.v3StakeKeypadValue = this.v3StakeKeypadValue ? `${this.v3StakeKeypadValue}.` : '0.'
		} else if (key !== '.') {
			this.v3StakeKeypadValue = `${this.v3StakeKeypadValue}${key}`
		}

		const stake = this.v3StakeKeypadValue === '' ? null : Number(this.v3StakeKeypadValue)
		if (stake === null || Number.isFinite(stake)) {
			this.placeBetForm.patchValue({ stake })
			this.placeBetForm.controls.stake.markAsDirty()
			this.placeBetForm.controls.stake.markAsTouched()
		}
	}

	clearCompactStake() {
		this.v3StakeKeypadValue = ''
		this.placeBetForm.patchValue({ stake: null })
		this.placeBetForm.controls.stake.markAsPristine()
		this.placeBetForm.controls.stake.markAsUntouched()
	}

	resetCompactForm() {
		const odd =
			this.selectedRunner().provider === MarketProvider.SB && this.selectedRunner().bettingType === 'LINE'
				? (this.selectedRunner().runs ?? this.selectedRunner().dfOdds)
				: this.selectedRunner().dfOdds

		this.placeBetForm.patchValue({ odd, stake: null })
		this.v3StakeKeypadValue = ''
		this.placeBetForm.markAsPristine()
		this.placeBetForm.markAsUntouched()
	}

	getCompactRunners(): { selectionId: number; runnerName: string }[] {
		return this.market().runners.map((runner) => ({ selectionId: runner.selectionId, runnerName: runner.runnerName }))
	}

	isCompactFancySelection(): boolean {
		return this.market().descriptionPrefix === 'Fancy'
	}

	getCompactPlacedProfitAndLossValue(selectionId: number): Observable<CompactRunnerPnl> {
		if (this.helperService.isDoubleChance(this.market())) {
			return this.betService.getPlacedDoubleChanceProfitAndLossValue(this.market().marketId, selectionId)
		}

		if (this.selectedRunner().provider === MarketProvider.SB && this.selectedRunner().bettingType !== 'LINE') {
			return this.betService.getPlacedProfitAndLossValueForBookmakerRunner(this.selectedRunner().marketGroupId, selectionId, false)
		}

		return this.betService.getPlacedProfitAndLossValueForBfRunner(this.market().marketId, selectionId, this.isLineMarket)
	}

	getCompactPredictedProfitAndLossValue(selectionId: number): Observable<CompactRunnerPnl> {
		if (this.helperService.isDoubleChance(this.market())) {
			return this.betService.getPredictedDoubleChanceProfitAndLossValue(this.market().marketId, selectionId)
		}

		if (this.selectedRunner().provider === MarketProvider.SB && this.selectedRunner().bettingType !== 'LINE') {
			return this.betService.getPredictedProfitAndLossValueForBookmakerRunner(this.selectedRunner().marketGroupId, selectionId, false)
		}

		return this.betService.getPredictedProfitAndLossValueForBfRunner(this.market().marketId, selectionId, this.isLineMarket)
	}

	getCompactProfitAndLossClass(profitAndLoss: CompactRunnerPnl): 'profit' | 'loss' | '' {
		const value = this.getCompactProfitAndLossComparisonValue(profitAndLoss)

		if (value > 0) return 'profit'
		if (value < 0) return 'loss'
		return ''
	}

	formatCompactProfitAndLoss(profitAndLoss: CompactRunnerPnl): string {
		if (Array.isArray(profitAndLoss)) {
			return profitAndLoss.map((value) => this.formatCompactProfitAndLossNumber(value)).join(', ')
		}

		if (profitAndLoss && typeof profitAndLoss === 'object') {
			return this.formatCompactProfitAndLossNumber(profitAndLoss.result)
		}

		return this.formatCompactProfitAndLossNumber(profitAndLoss ?? 0)
	}

	private getCompactProfitAndLossComparisonValue(profitAndLoss: CompactRunnerPnl): number {
		if (Array.isArray(profitAndLoss)) {
			return profitAndLoss.find((value) => value !== 0) ?? 0
		}

		if (profitAndLoss && typeof profitAndLoss === 'object') {
			return profitAndLoss.result
		}

		return profitAndLoss ?? 0
	}

	private formatCompactProfitAndLossNumber(value: number): string {
		return this.compactProfitAndLossFormatter.format(value)
	}

	/**
	 * Increment the value of the placed stake by a given amount
	 * @param amount
	 */
	incrementStakeByAmount(amount = 0.5) {
		const stakeValue = Number(this.placeBetForm.controls.stake.value)
		const stake = Number(Number((stakeValue ?? 0) + amount).toFixed(2))

		this.v3StakeKeypadValue = String(stake)
		this.placeBetForm.patchValue({ stake })
	}

	/**
	 * Decrement the value of the placed stake by a given amount
	 * @param amount
	 */
	decrementStakeByAmount(amount = 0.5) {
		let result = 0.1
		if (Number(this.placeBetForm.controls.stake.value ?? 0) - amount > 0.1) {
			result = +(this.placeBetForm.controls.stake.value ?? 0) - amount
		}

		const stake = Number(result.toFixed(2))

		this.v3StakeKeypadValue = String(stake)
		this.placeBetForm.patchValue({ stake })
	}

	/**
	 * Increment the value of the placed odd by a given amount
	 */
	incrementOddByAmount() {
		if (!this.isLineMarket) {
			let amount = 0.01

			if (this.placeBetForm.controls.odd.value) {
				amount = this.helperService.getOddsIncrement(Number(this.placeBetForm.controls.odd.value))
			}

			this.placeBetForm.patchValue({
				odd: Number((Number(this.placeBetForm.controls.odd.value!) + amount).toFixed(2)),
			})
		}
	}

	/**
	 * Decrement the value of the placed odd by a given amount
	 */
	decrementOddByAmount() {
		if (!this.isLineMarket) {
			let amount = 0.01

			if (this.placeBetForm.controls.odd.value) {
				amount = this.helperService.getOddsDecrement(Number(this.placeBetForm.controls.odd.value))
			}

			let result = 1.01
			if (Number(this.placeBetForm.controls.odd.value ?? 0) - amount > 1.01) {
				result = +(this.placeBetForm.controls.odd.value ?? 0) - amount
			}

			this.placeBetForm.patchValue({ odd: Number(result.toFixed(2)) })
		}
	}

	removeCurrentPredictedBet() {
		const currentPredictedBet = this.betService.predictedBets$.getValue()
		const debuggingKey = `${this.market().event?.name} - ${this.market().marketName} - -1`.toUpperCase()

		if (currentPredictedBet[this.selectedRunner().marketGroupId]) {
			delete currentPredictedBet[this.selectedRunner().marketGroupId]
			this.betService.predictedBets$.next(currentPredictedBet)
			this.debuggingService.removeExposureDebuggingObject(`${this.market().event?.name} - ${this.market().marketName}`.toUpperCase())
		}

		if (this.debuggingService.exposureDebuggingObject[debuggingKey]) {
			delete this.debuggingService.exposureDebuggingObject[debuggingKey]
		}
	}

	showDebuggingAlert() {
		const message = JSON.stringify(this.debuggingService.exposureDebuggingObject)
		this.debuggingService.showDebuggingAlert(message)
	}

	getTotalExposureDebugging() {
		return this.debuggingService.exposureDebuggingObject[`${this.market().event?.name} - ${this.market().marketName} - -1`.toUpperCase()]?.newTotalExposure
	}

	sideLabel(label: 'BACK' | 'LAY') {
		const { provider, bettingType } = this.selectedRunner() || {}

		if (provider === MarketProvider.SB && bettingType === 'LINE') {
			return label === 'BACK' ? 'YES' : 'NO'
		} else if (provider === MarketProvider.BF && bettingType === 'LINE') {
			return label === 'BACK' ? 'Under' : 'Over'
		}

		return label
	}

	/**
	 * Handle changing the odd on place bet window which will result
	 * change in the predicted bet live profit and loss
	 * @param odd
	 * @param stake
	 */
	private onStakeAndOddValueChange(odd: number | null, stake: number | null) {
		const oddForCalculation =
			this.selectedRunner().provider === MarketProvider.SB && this.selectedRunner().bettingType === 'LINE' ? this.selectedRunner().dfOdds.toString() : odd
		const predictedBets = this.betService.predictedBets$.getValue()
		const currentPredictedBet: PredictedBet = {
			marketGroupId: this.selectedRunner().marketGroupId,
			marketId: this.selectedRunner().marketId,
			marketName: this.selectedRunner().oddMarketName,
			selectionId: this.selectedRunner().selectionId,
			side: this.selectedRunner().side,
			fOdds: this.helperService.getFinalOdds(
				Number(oddForCalculation ?? 1),
				this.selectedRunner().autoBm ? MarketProvider.BF : this.selectedRunner().provider,
				this.selectedRunner().bettingType === 'LINE',
			),
			stake: Number(stake ?? 0),
			bettingType: this.selectedRunner().bettingType,
			dfOdds: this.selectedRunner().runs ?? this.selectedRunner().dfOdds,
			rangeOdd: this.selectedRunner().runs,
			provider: this.selectedRunner().provider,
			handicap: this.selectedRunner().handicap,
			handicapValues: this.selectedRunner().handicapValues,
			averagePriceMatched: this.helperService.getFinalOdds(
				Number(oddForCalculation ?? 1),
				this.selectedRunner().autoBm ? MarketProvider.BF : this.selectedRunner().provider,
				this.selectedRunner().bettingType === 'LINE',
			),
			runnerName: this.selectedRunner().runnerName,
		}

		this.placeBetModalService.currentPlacement$.next({ ...this.selectedRunner(), ...currentPredictedBet })

		this.betService.predictedBets$.next({
			...predictedBets,
			[currentPredictedBet.marketGroupId]: currentPredictedBet,
		})

		this.profitOrLiability.update(() => {
			if (!this.placeBetForm.controls.stake.value || !this.placeBetForm.controls.odd.value) {
				return null
			}

			const odd = this.helperService.getFinalOdds(
				Number(oddForCalculation ?? 1),
				this.selectedRunner().autoBm ? MarketProvider.BF : this.selectedRunner().provider,
				this.selectedRunner().bettingType === 'LINE',
			)

			if (this.selectedRunner().provider === MarketProvider.SB && this.selectedRunner().bettingType === 'LINE') {
				return (odd - 1) * Number(stake ?? 0)
			} else if (this.selectedRunner().side === 'BACK') {
				return this.betService.calculatePotentialProfit(Number(stake ?? 0), odd, this.selectedRunner().side, odd, this.selectedRunner().bettingType)
			} else {
				return this.betService.calculatePotentialLoss(Number(stake ?? 0), odd, this.selectedRunner().side, odd, this.selectedRunner().bettingType)
			}
		})
	}

	/**
	 * Handle bet placement response
	 */
	private async handleBetPlacementResponse(result: PlaceBetWebsocketResponse, betId: number) {
		const betStatusData = result.data

		if (betStatusData) {
			if (betStatusData.ID && betStatusData.ID === betId) {
				if (betStatusData.STATUS === 'Success') {
					// Fully Matched
					if (betStatusData.SIZE_MATCHED > 0 && betStatusData.SIZE_REMAINING === 0) {
						this.toastService.showToast(TOAST_STATE.SUCCESS, `Bet Placed and Matched`)
					}
					// Partially Matched
					else if (betStatusData.SIZE_MATCHED > 0 && betStatusData.SIZE_REMAINING > 0) {
						this.toastService.showToast(TOAST_STATE.WARNING, `Bet Placed but Partially Matched`)
					}
					// Unmatched
					else if (betStatusData.SIZE_MATCHED === 0) {
						this.toastService.showToast(TOAST_STATE.WARNING, `Bet Placed but Unmatched`)
					}

					this.closeBetPlacingWindow()
					this.removeCurrentPredictedBet()
					// Reset isPlacingBet flag on success
					this.betService.isPlacingBet.set(false)
				} else {
					this.toastService.showToast(TOAST_STATE.DANGER, result.data.BF_MESSAGE ?? 'Error in bet placement')
					this.betService.fetchCurrentUserOrders().subscribe()
					// Reset isPlacingBet flag on error
					this.betService.isPlacingBet.set(false)
				}
			}
		}

		if (this.betPlacementTimeout) clearTimeout(this.betPlacementTimeout)
		this.placedCustomerOrderRef = null
	}

	private async handleBetPlacementTimeout(customerOrderRef: string | null) {
		// Get the network status at the moment of bet placement timeout
		const networkStatus = await Network.getStatus()

		// If the placed bet's customer order ref is found
		if (customerOrderRef) {
			// If network is in connected state
			if (networkStatus.connected) {
				// Try to get the placed bet from the current user order's list of bets
				const placedBet = await this.getBetStatus(customerOrderRef)

				// If the bet was found in the list
				if (placedBet) {
					// Show a success message
					this.toastService.showToast(
						TOAST_STATE.SUCCESS,
						`Bet Placed and Matched ${placedBet.runnerName}, ${placedBet.betSize} @ ${placedBet.averagePriceMatched}`,
					)

					// Refresh the exposure
					const walletId = this.helperService.getWalletId()
					if (walletId) {
						this.balanceService.refreshExposure(walletId).subscribe()
					}
				} else {
					// If bet was not found, show bet placement timeout error message
					this.toastService.showToast(TOAST_STATE.DANGER, 'Bet placement timed out.')
				}
			} else {
				// If connection was not in connected state, show an error message with a retry button that will restart this whole process again
				this.toastService.showToast(TOAST_STATE.DANGER, 'Network error.', [
					{
						text: 'Retry',
						handler: () => this.handleBetPlacementTimeout(this.placedCustomerOrderRef),
					},
				])
			}
		} else {
			// If the placed bet's customer ref id is not found, show bet placement timeout error message
			this.toastService.showToast(TOAST_STATE.DANGER, 'Bet placement timed out.')
		}

		// To notify that the bet placement process has ended
		this.betService.isPlacingBet.set(false)
	}

	/**
	 * Look for a specific bet inside the current user orders based on currentOrderRef attribute
	 * @param customerOrderRef
	 * @private
	 */
	private async getBetStatus(customerOrderRef: string) {
		return await firstValueFrom(this.betService.fetchBetStatus({ customerOrderRef }))
	}

	private updateFormControlValues() {
		const userSettings = this.helperService.getUserData()?.user_fields?.USER_BETSLIP
		const appearance = userSettings?.betSlipSettings?.appearance
		const defaultStake = userSettings?.betSlipSettings?.defaultStake

		// Apply different validators based on the market provider
		if (this.selectedRunner().provider === MarketProvider.BF && this.selectedRunner().bettingType !== 'LINE' && !this.isHandicapTypeMarket) {
			this.formControls.odd.addValidators(validOddValidator())
		}

		// Assign initial values to the form controls

		const odd =
			this.selectedRunner().provider === MarketProvider.SB && this.selectedRunner().bettingType === 'LINE'
				? (this.selectedRunner().runs ?? this.selectedRunner().dfOdds)
				: this.selectedRunner().dfOdds
		const stake = defaultStake && appearance === 'oneClickBetting' ? this.helperService.getNumericValue(defaultStake) : null

		// Add the current market to the betsMarkets list of markets for later use for exposure calculation
		this.betService.betsMarkets$.next({
			...this.betService.betsMarkets$.getValue(),
			[this.market().marketId]: this.market(),
		})

		if (userSettings?.quickBetAmounts) {
			this.editStakeModalService.quickBetAmounts$.next(userSettings.quickBetAmounts)
		}

		this.v3StakeKeypadValue = stake === null ? '' : String(stake)
		this.placeBetForm.patchValue({ odd, stake })
	}

	private subscribeToFormControlsValueChanges() {
		this.subscriptions$.add(
			combineLatest([this.placeBetForm.controls.odd.valueChanges, this.placeBetForm.controls.stake.valueChanges]).subscribe({
				next: ([odd, stake]) => this.onStakeAndOddValueChange(odd, stake),
			}),
		)
	}

	private subscribeToExposureEvaluation() {
		combineLatest([
			this.betService.predictedBets$,
			this.balanceService.balanceData$,
			this.betService.matchedNonPendingBets$,
			this.placeBetModalService.selectedRunner$,
			this.formControls.stake.valueChanges,
			this.formControls.odd.valueChanges,
		])
			.pipe(
				map(([predictedBets, balance, matchedNonPendingBets, selectedRunner]) => {
					if (balance && selectedRunner && predictedBets[selectedRunner.marketGroupId]?.stake && predictedBets[selectedRunner.marketGroupId]?.fOdds) {
						const bets = matchedNonPendingBets.map((bet) => ({
							...bet,
							betSize: bet.sizeRemaining === 0 ? (bet.stakeMatched ? bet.stakeMatched : bet.betSize) : bet.betSize,
							marketGroupId: this.helperService.generateMarketGroupIdForCalculationBet(bet),
							dfOdds: bet.provider === MarketProvider.BF && bet.bettingType === 'LINE' ? bet.averagePriceMatched : bet.dfOdds,
							rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
							sizeMatched: bet.stakeMatched,
						}))

						return this.exposureService.validate(bets, balance, predictedBets[selectedRunner.marketGroupId], '-1')
					}

					return null
				}),
			)
			.subscribe({
				next: (validationResult) => {
					// Set both properties based on validation result
					this.insufficientBalanceError.set(validationResult && 'insufficientBalance' in validationResult ? validationResult : null)
					this.shouldCancelUnmatchedBetsFirst.set(
						Boolean(validationResult && 'cancelUnmatchedBetsFirst' in validationResult && validationResult.cancelUnmatchedBetsFirst),
					)
				},
			})
	}

	private setValidators() {
		const market = this.market()

		if (
			(!this.helperService.marketIsBookmakerType(market) && !this.helperService.marketIsFancyType(market)) ||
			(this.helperService.marketIsBookmakerType(market) && (market.minBet ?? 0) <= 0) ||
			(this.helperService.marketIsFancyType(market) && !market.runners[0]?.minBetSize)
		) {
			this.stakeValidators['min'] = Validators.min(Number(this.helperService.getWhiteLabelBetslipSettings().quickBetAmounts[0]))
			this.formControls.stake.setValidators(Object.values(this.stakeValidators).flat())
			this.formControls.stake.updateValueAndValidity()
		}
	}

	private setBetConfigValidators(betConfig: Record<string, unknown> | null) {
		const market = this.market()

		const sportId = market.eventType?.id
		const eventId = market.eventId ?? market.event?.id
		if (!betConfig || !sportId) return

		let marketType: 'autoBm' | 'bookmaker' | 'fancy' | undefined
		if (this.selectedRunner().autoBm) {
			marketType = 'autoBm'
		} else if (this.helperService.marketIsBookmakerType(market)) {
			marketType = 'bookmaker'
		} else if (this.helperService.marketIsFancyType(market)) {
			marketType = 'fancy'
		}

		const config = this.betConfigService.resolveConfig(betConfig, sportId, marketType, eventId)
		if (!config) return

		const { min_bet_size, max_bet_size } = config

		if (min_bet_size) {
			this.stakeValidators['min'] = Validators.min(min_bet_size)
			this.minBetSize.set(min_bet_size)
		}
		if (max_bet_size) {
			this.stakeValidators['max'] = Validators.max(max_bet_size)
			this.maxBetSize.set(max_bet_size)
		}

		this.formControls.stake.setValidators(Object.values(this.stakeValidators).flat())
		this.formControls.stake.updateValueAndValidity()
	}
}
